import { nanoid } from 'nanoid'
import jp from 'jsonpath'
import {
  AiFeatureResult,
  AiResult,
  AiResultScore,
  AiResultStatus,
  SegmentedResult,
  SegmentMetadata,
  SegmentResult,
  StructuredSummaryResult,
  StructuredSummaryResultType,
} from '../../../models/AiResult'
import { AiFeature } from '../../../models/AiFeature'
import { AiFeaturePreset } from '../../../models/AiFeaturePreset'

export type AIResultDisplayModelResult =
  | string
  | number
  | boolean
  | {
      [key: string]: AIResultDisplayModel | AIResultDisplayModel[]
    }

export interface AIResultDisplayModel {
  aiResultId: string
  aiFeatureId: string
  dismissed: boolean
  result: AIResultDisplayModelResult
  isLeaf?: boolean
  level: number
  displayId: string
  selected: boolean
}

export interface TopicDetectionResultViewModel {
  type: 'topicDetection'
  result: AIResultDisplayModel[]
  confidence: number
  context: string
}

export interface QuestionAnswerResultViewModel {
  type: 'questionAnswer'
  questionId: string
  result: AIResultDisplayModel[]
  confidence: number
  context: string
}

export interface StructuredSummaryResultViewModel {
  type: 'structuredSummary'
  result: AIResultDisplayModel[]
}

export interface SegmentResultViewModel {
  results: IndividualAIResultViewModel[]
  metadata: SegmentMetadata
}

export interface SegmentedResultViewModel {
  type: 'segmented'
  segmentResults: SegmentResultViewModel[]
}

export type ComplexAIResultViewModel = SegmentedResultViewModel
export type IndividualAIResultViewModel = TopicDetectionResultViewModel | QuestionAnswerResultViewModel | StructuredSummaryResultViewModel
export type AIFeatureResultViewModel = IndividualAIResultViewModel | ComplexAIResultViewModel

export interface AIResultViewModel {
  id: string
  aiFeatureId: string
  createdAt: string
  userId: string | null
  result: AIFeatureResultViewModel | null
  roomId: string
  sessionId: string
  status: AiResultStatus
  scores: AiResultScore[]
  parsers: Parser[]
}

interface Parser {
  jsonPath: string
  type: string
}

const getParsers = (aiResult: AiResult, aiFeatures: AiFeature[], aiFeaturePresets: AiFeaturePreset[]): Parser[] => {
  if (aiResult.originalResult) {
    return []
  }
  try {
    const aiFeature = aiFeatures.find((aiFeature) => aiFeature.id === aiResult.aiFeatureId)
    const aiFeaturePreset = aiFeature?.preset && aiFeaturePresets.find((aiFeaturePreset) => aiFeaturePreset.id === aiFeature.preset?.id)
    if (aiFeaturePreset) {
      return jp.query(aiFeaturePreset, '$.aiResultTypesSchema.properties.*.parsers.*') ?? []
    }
  } catch (err) {
    console.log(err)
  }
  return []
}

const parseDate = (value: string) => {
  const date = new Date(value)
  if (isNaN(date.getTime())) {
    return value
  }
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }
  if (date.getHours()) {
    options.hour = 'numeric'
    options.minute = 'numeric'
    options.hour12 = true
  }
  return date.toLocaleDateString('en-US', options)
}

const applyParsers = (aiResult: AiResult, parsers: Parser[]): AiResult => {
  if (aiResult.originalResult) {
    return aiResult
  }
  try {
    parsers.forEach((parser) => {
      if (parser.type === 'date') {
        jp.apply(aiResult, parser.jsonPath, parseDate)
      }
    })
  } catch (err) {
    console.log(err)
  }
  return aiResult
}

const reverseParsers = (result: string | number, parsers: Parser[]): string | number => {
  try {
    parsers.reverse().forEach((parser) => {
      if (parser.type === 'date') {
        result = new Date(result).toISOString()
      }
    })
  } catch (err) {
    console.log(err)
  }
  return result
}

export const mapAiResultsToAiResultViewModel = (aiResults: AiResult[], aiFeatures: AiFeature[], aiFeaturePresets: AiFeaturePreset[]): AIResultViewModel[] => {
  return aiResults
    .map((newResult) => {
      const parsers = getParsers(newResult, aiFeatures, aiFeaturePresets)
      const parsedResult = applyParsers(newResult, parsers)
      if (newResult.status === 'no_result') {
        return {
          id: newResult.id,
          aiFeatureId: newResult.aiFeatureId,
          createdAt: newResult.createdAt,
          userId: newResult.userId,
          result: null,
          roomId: newResult.roomId,
          sessionId: newResult.sessionId,
          status: newResult.status,
          scores: newResult.scores,
          parsers: parsers,
        } as AIResultViewModel
      } else if (newResult.result && newResult.result.type === 'structuredSummary') {
        return mapStructuredSummaryResultToAiResultViewModel(parsedResult, parsers)
      } else if (newResult.result && newResult.result.type === 'segmented') {
        return mapSegmentedResultToAiResultViewModel(parsedResult, parsers)
      }
      return []
    })
    .flat()
}

const mapStructuredSummaryResultToAiResultViewModel = (aiResult: AiResult, parsers: Parser[]): AIResultViewModel => {
  return mapStructuredSummaryResult(aiResult, parsers)
}

const mapSegmentedResultToAiResultViewModel = (aiResult: AiResult, parsers: Parser[]): AIResultViewModel => {
  const segmentResults = (aiResult.result as SegmentedResult).segmentResults
  return {
    ...aiResult,
    result: {
      type: 'segmented',
      segmentResults: segmentResults.map((segmentResult) => {
        return {
          ...segmentResult,
          results: segmentResult.results
            .map((result) => {
              if (result.type === 'structuredSummary') {
                return {
                  type: 'structuredSummary',
                  result: ([] as AIResultDisplayModel[]).concat(mapStructuredSummaryResultTypeObjectToDisplayModel(aiResult, result.result)),
                } as IndividualAIResultViewModel
              }
              return {} as IndividualAIResultViewModel
            })
            .flat(),
        }
      }),
    },
    parsers: parsers,
  }
}

const mapStructuredSummaryResult = (aiResult: AiResult, parsers: Parser[], level: number = 0): AIResultViewModel => {
  const structuredSummaryResult = aiResult.result as StructuredSummaryResult
  if (Array.isArray(structuredSummaryResult.result)) {
    if (typeof structuredSummaryResult.result[0] === 'string') {
      return {
        ...aiResult,
        result: {
          type: 'structuredSummary',
          result: (structuredSummaryResult.result as string[]).map((result) => {
            return {
              aiFeatureId: aiResult.aiFeatureId,
              aiResultId: aiResult.id,
              dismissed: false,
              result: result,
              isLeaf: false,
              level: level + 1,
              displayId: nanoid(),
              selected: false,
            }
          }),
        },
        parsers: parsers,
      }
    } else {
      return {
        ...aiResult,
        result: {
          type: 'structuredSummary',
          result: structuredSummaryResult.result.map((result) => {
            return {
              aiFeatureId: aiResult.aiFeatureId,
              aiResultId: aiResult.id,
              dismissed: false,
              result: Object.entries(result).reduce(
                (acc, [key, value]) => {
                  acc[key] = mapStructuredSummaryResultTypeObjectToDisplayModel(aiResult, value, level + 1) as AIResultDisplayModel
                  return acc
                },
                {} as { [key: string]: AIResultDisplayModel },
              ),
              isLeaf: false,
              level: level + 1,
              displayId: nanoid(),
              selected: false,
            }
          }),
        },
        parsers: parsers,
      }
    }
  }
  if (typeof structuredSummaryResult.result === 'object') {
    return {
      ...aiResult,
      result: {
        type: 'structuredSummary',
        result: Object.entries(structuredSummaryResult.result).map(([key, value]) => {
          return {
            dismissed: false,
            result: { [key]: mapStructuredSummaryResultTypeObjectToDisplayModel(aiResult, value, level + 1) },
            isLeaf: false,
            level: level + 1,
            displayId: nanoid(),
          } as AIResultDisplayModel
        }),
      },
      parsers: parsers,
    }
  }
  return {
    ...aiResult,
    result: {
      type: 'structuredSummary',
      result: [
        {
          aiFeatureId: aiResult.aiFeatureId,
          aiResultId: aiResult.id,
          dismissed: false,
          result: structuredSummaryResult.result,
          isLeaf: false,
          level: level + 1,
          displayId: nanoid(),
          selected: false,
        },
      ],
    },
    parsers: parsers,
  }
}

const mapStructuredSummaryResultTypeObjectToDisplayModel = (
  aiResult: AiResult,
  structuredSummaryResult: StructuredSummaryResultType,
  level: number = 0,
): AIResultDisplayModel | AIResultDisplayModel[] => {
  if (Array.isArray(structuredSummaryResult)) {
    if (typeof structuredSummaryResult[0] === 'string') {
      return (structuredSummaryResult as string[]).map((result) => {
        return {
          aiFeatureId: aiResult.aiFeatureId,
          aiResultId: aiResult.id,
          dismissed: false,
          result: result,
          isLeaf: false,
          level: level + 1,
          displayId: nanoid(),
          selected: false,
        }
      })
    } else {
      return structuredSummaryResult
        .map((result) => {
          return {
            aiFeatureId: aiResult.aiFeatureId,
            aiResultId: aiResult.id,
            dismissed: false,
            result: Object.entries(structuredSummaryResult).reduce(
              (acc, [key, value]) => {
                acc[key] = [mapStructuredSummaryResultTypeObjectToDisplayModel(aiResult, value, level + 1) as AIResultDisplayModel]
                return acc
              },
              {} as { [key: string]: AIResultDisplayModel[] },
            ),
            isLeaf: false,
            level: level + 1,
            displayId: nanoid(),
            selected: false,
          }
        })
        .flat()
    }
  }
  if (typeof structuredSummaryResult === 'object') {
    return Object.entries(structuredSummaryResult).map(([key, value]) => {
      return {
        aiResultId: aiResult.id,
        dismissed: false,
        result: { [key]: mapStructuredSummaryResultTypeObjectToDisplayModel(aiResult, value, level + 1) },
        isLeaf: false,
        level: level + 1,
        displayId: nanoid(),
        selected: false,
      } as AIResultDisplayModel
    })
  }
  return {
    aiFeatureId: aiResult.aiFeatureId,
    aiResultId: aiResult.id,
    dismissed: false,
    result: structuredSummaryResult as string,
    isLeaf: false,
    level: level + 1,
    displayId: nanoid(),
    selected: false,
  }
}

export const mapAIResultViewModelToAIResult = (
  aiFeatureResultViewModel: AIFeatureResultViewModel,
  parsers: Parser[],
  skipDismissed: boolean = true,
): AiFeatureResult | null => {
  if (aiFeatureResultViewModel.type === 'structuredSummary') {
    const result = mapStructuredSummaryAIFeatureResultViewModelToModel(aiFeatureResultViewModel, skipDismissed, parsers)
    if (result && (Array.isArray(result.result) ? result.result.length : result.result !== undefined && !!result.result)) {
      return result
    }
  }
  if (aiFeatureResultViewModel.type === 'segmented') {
    const result = mapSegmentedAIFeatureResultViewModelToModel(aiFeatureResultViewModel, skipDismissed, parsers)
    if (result && result.segmentResults.length) {
      return result
    }
  }
  return null
}

const mapSegmentedAIFeatureResultViewModelToModel = (
  aiResultViewModel: SegmentedResultViewModel,
  skipDismissed: boolean,
  parsers: Parser[],
): SegmentedResult | null => {
  const segmentResults = aiResultViewModel.segmentResults.map((segmentResult) => {
    const results = segmentResult.results
      .map((result) => {
        if (result.type === 'structuredSummary') {
          const mappedResult = mapStructuredSummaryAIFeatureResultViewModelToModel(result, skipDismissed, parsers)
          if (mappedResult) {
            return mappedResult
          }
          return null
        }
        return null
      })
      .filter((r) => r && (Array.isArray(r.result) ? r.result.length : r.result)) as StructuredSummaryResult[]
    return {
      metadata: segmentResult.metadata,
      results: results,
    }
  }) as SegmentResult[]
  const filteredSegmentResults = segmentResults.filter((sr) => sr.results.length) as SegmentResult[]
  if (filteredSegmentResults.length) {
    return {
      type: 'segmented',
      segmentResults: filteredSegmentResults,
    }
  }
  return null
}

const mapStructuredSummaryAIFeatureResultViewModelToModel = (
  aiResultViewModel: StructuredSummaryResultViewModel,
  skipDismissed: boolean,
  parsers: Parser[],
): StructuredSummaryResult | null => {
  if (aiResultViewModel.result.length > 1) {
    if (typeof aiResultViewModel.result[0].result === 'object') {
      let newObj = {}
      const listOfMapsArray: object[] = []
      aiResultViewModel.result.forEach((r) => {
        if (skipDismissed && r.dismissed) {
          return
        }
        const resultObj = mapAIResultDisplayModelToStructuredSummaryResultType(r, skipDismissed, parsers) as {
          [key: string]: StructuredSummaryResultType
        }
        if (resultObj) {
          newObj = { ...newObj, ...resultObj }
          listOfMapsArray.push(resultObj)
        }
      })
      const isListOfMaps = listOfMapsArray
        .map((o) => Object.keys(o))
        .flat()
        .some((element, index, arr) => {
          return arr.indexOf(element) !== index
        })
      if (isListOfMaps) {
        return { type: 'structuredSummary', result: listOfMapsArray as StructuredSummaryResultType, formattedResults: {} }
      } else {
        return { type: 'structuredSummary', result: newObj, formattedResults: {} }
      }
    }

    return {
      type: 'structuredSummary',
      result: aiResultViewModel.result
        .map((r) => {
          if (skipDismissed && r.dismissed) {
            return null
          }
          return r.result as string
        })
        .filter((r) => r) as string[],
      formattedResults: {},
    }
  }

  const aiResultDisplayModel = aiResultViewModel.result[0]

  if (typeof aiResultDisplayModel.result === 'object') {
    const result = mapAIResultDisplayModelToStructuredSummaryResultType(aiResultDisplayModel, skipDismissed, parsers)
    if (result) {
      if (Object.keys(result).length > 1) {
        return { type: 'structuredSummary', result: [result] as StructuredSummaryResultType, formattedResults: {} }
      }
      return {
        type: 'structuredSummary',
        result: result,
        formattedResults: {},
      }
    }
    return null
  }
  if (skipDismissed && aiResultDisplayModel.dismissed) {
    return null
  }
  return { type: 'structuredSummary', result: aiResultDisplayModel.result as boolean | number | string, formattedResults: {} }
}

const mapAIResultDisplayModelToStructuredSummaryResultType = (
  displayModel: AIResultDisplayModel | AIResultDisplayModel[],
  skipDismissed: boolean,
  parsers: Parser[],
): StructuredSummaryResultType | null => {
  if (Array.isArray(displayModel)) {
    return displayModel
      .map((v) => {
        return mapAIResultDisplayModelToStructuredSummaryResultType(v, skipDismissed, parsers)
      })
      .filter((r) => r) as string[]
  }
  if (typeof displayModel.result === 'object') {
    const obj: StructuredSummaryResultType = {}
    Object.entries(displayModel.result).forEach(([k, v]) => {
      const objectResult = mapAIResultDisplayModelToStructuredSummaryResultType(v, skipDismissed, parsers)
      if (objectResult) {
        obj[k] = objectResult
      }
    })
    if (Object.keys(obj).length) {
      return obj
    }
    return null
  }
  if (skipDismissed && displayModel.dismissed) {
    return null
  }
  return reverseParsers(displayModel.result as string | number, parsers)
}
